import { forwardRef, useEffect, useRef, useState } from 'react'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { BaccaratBetPanel } from '@/components/original/games/ui/BaccaratBetPanel'
import { BaccaratTable } from '@/components/original/games/baccarat/components/BaccaratTable'
import { AssetManager } from '@/components/original/games/baccarat/core/AssetManager'
import { GameController } from '@/components/original/games/baccarat/core/GameController'
import { SoundManager } from '@/components/original/games/baccarat/core/SoundManager'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { dismissBaccaratResult, resetBaccaratGame, selectBaccaratGame, setBaccaratCurrencyCode } from '@/store/slices/baccaratSlice'

interface BaccaratGameProps {
	currencyCode: string
	className?: string
}

export const BaccaratGame = forwardRef<HTMLDivElement, BaccaratGameProps>(function BaccaratGame({ currencyCode, className }, ref) {
	const dispatch = useAppDispatch()
	const baccarat = useAppSelector(selectBaccaratGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const controllerRef = useRef<GameController | null>(null)
	const [controller, setController] = useState<GameController | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [loadError, setLoadError] = useState(false)
	const [loadProgress, setLoadProgress] = useState(0)

	useEffect(() => {
		dispatch(setBaccaratCurrencyCode(currencyCode))
	}, [currencyCode, dispatch])

	useEffect(() => {
		let cancelled = false

		const boot = async () => {
			setIsLoading(true)
			setLoadError(false)

			try {
				await AssetManager.loadAll((progress) => {
					if (!cancelled) {
						setLoadProgress(Math.round(progress * 100))
					}
				})
				await SoundManager.instance.load()

				if (cancelled) {
					return
				}

				const gameController = new GameController()
				controllerRef.current = gameController
				setController(gameController)
				setIsLoading(false)
			} catch {
				if (!cancelled) {
					setLoadError(true)
					setIsLoading(false)
				}
			}
		}

		void boot()

		return () => {
			cancelled = true
			controllerRef.current?.dispose()
			controllerRef.current = null
			setController(null)
			SoundManager.instance.stopAll()
			dispatch(resetBaccaratGame())
		}
	}, [dispatch])

	return (
		<div
			ref={ref}
			className={cn(
				'flex w-full min-w-0 flex-col gap-4 xl:flex-row',
				'[&:fullscreen]:fixed [&:fullscreen]:inset-0 [&:fullscreen]:z-[9999] [&:fullscreen]:flex [&:fullscreen]:h-screen [&:fullscreen]:w-screen [&:fullscreen]:flex-col [&:fullscreen]:gap-4 [&:fullscreen]:bg-background [&:fullscreen]:p-4 xl:[&:fullscreen]:flex-row',
				className,
			)}
		>
			<div className='order-2 h-[420px] w-full shrink-0 overflow-hidden sm:h-[520px] xl:order-1 xl:h-[520px] xl:w-[340px] 2xl:w-[360px] [:fullscreen_&]:order-2 [:fullscreen_&]:h-auto [:fullscreen_&]:max-h-[42%] [:fullscreen_&]:w-full [:fullscreen_&]:shrink-0 xl:[:fullscreen_&]:order-1 xl:[:fullscreen_&]:h-full xl:[:fullscreen_&]:max-h-none xl:[:fullscreen_&]:w-[340px]'>
				<BaccaratBetPanel controller={controller} className='h-full min-h-0 [:fullscreen_&]:h-full' />
			</div>

			<div
				className={cn(
					'relative order-1 w-full min-w-0 overflow-hidden rounded-2xl border border-border bg-card',
					'aspect-[5/4]',
					'xl:order-2 xl:aspect-[1024/622] xl:flex-1',
					'[:fullscreen_&]:order-1 [:fullscreen_&]:aspect-auto [:fullscreen_&]:h-auto [:fullscreen_&]:min-h-0 [:fullscreen_&]:flex-1 xl:[:fullscreen_&]:order-2 xl:[:fullscreen_&]:h-full',
				)}
			>
				{isLoading ? (
					<div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card/90'>
						<BetsCycleLoader size={32} />
						<p className='text-sm font-medium text-muted-foreground'>Loading baccarat assets… {loadProgress}%</p>
					</div>
				) : null}

				{loadError ? (
					<div className='absolute inset-0 z-10 flex items-center justify-center bg-card p-6'>
						<p className='text-sm text-muted-foreground'>Unable to load the baccarat game right now.</p>
					</div>
				) : null}

				{!isLoading && !loadError ? (
					<BaccaratTable
						playerHand={baccarat.playerHand}
						bankerHand={baccarat.bankerHand}
						playerScore={baccarat.playerScore}
						bankerScore={baccarat.bankerScore}
						pendingChips={baccarat.pendingChips}
						winner={baccarat.winner}
						winningPlaces={baccarat.winningPlaces}
						betResults={baccarat.betResults}
						status={baccarat.status}
						isClearing={baccarat.isClearing}
						isSubmitting={baccarat.isSubmitting}
						isAuthenticated={isAuthenticated}
						isSpotHighlighting={baccarat.isSpotHighlighting}
						isResultVisible={baccarat.isResultVisible}
						multiplier={baccarat.currentMultiplier}
						profit={baccarat.currentProfit}
						currencyCode={baccarat.currencyCode}
						onChoosePlace={(place) => controller?.placeBet(place)}
						onUndo={() => controller?.undoBet()}
						onClear={() => controller?.clearBets()}
						onDismissResult={() => dispatch(dismissBaccaratResult())}
					/>
				) : null}
			</div>
		</div>
	)
})
